import { cn } from '@client/utils/styling/cn';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import { Tooltip } from 'antd';
import { ExternalLink } from 'lucide-react';
import { forwardRef, useMemo } from 'react';
import type { OpenInNewTabLinkProps } from './types';

const sizeClasses = {
    micro: 'btn-micro',
    small: 'btn-sm',
    middle: 'btn-md',
    large: 'btn-lg',
} as const;

const iconSizes = {
    micro: 12,
    small: 14,
    middle: 16,
    large: 20,
} as const;

/**
 * Base anchor component for opening links in a new tab
 * Styled as an accent icon button using ButtonIcon CSS classes
 */
const OpenInNewTabBase = forwardRef<HTMLAnchorElement, OpenInNewTabLinkProps>(
    (
        { color = 'accent', size = 'middle', tooltip = 'Open in new tab', disabled, className, onClick, ...props },
        ref,
    ) => {
        const linkClasses = useMemo(
            () =>
                cn(
                    'btn-variant',
                    'btn-icon-colored',
                    `btn-color-${color}`,
                    sizeClasses[size],
                    disabled && 'pointer-events-none opacity-50',
                    className,
                ),
            [color, size, disabled, className],
        );

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            // Stop propagation to prevent parent click handlers (e.g., row click) from firing
            e.stopPropagation();
            onClick?.(e);
        };

        const link = (
            <a
                ref={ref}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={disabled}
                className={linkClasses}
                onClick={handleClick}
                {...props}
            >
                <span className="btn-icon">
                    <ExternalLink size={iconSizes[size]} />
                </span>
            </a>
        );

        if (tooltip) {
            return <Tooltip title={tooltip}>{link}</Tooltip>;
        }

        return link;
    },
);

OpenInNewTabBase.displayName = 'OpenInNewTabBase';

/**
 * Button.OpenInNewTab - Type-safe icon link that opens in a new tab
 *
 * Uses TanStack Router for type-safe `to`, `params`, and `search` props.
 * Renders as an anchor with target="_blank" for new tab navigation.
 * Styled as an accent icon button by default, customisable via color prop.
 *
 * @example
 * // Navigate to a claim details page in new tab
 * <Button.OpenInNewTab
 *   to="/console/$orgId/claims/$claimId"
 *   params={{ orgId: '1', claimId: '123' }}
 *   tooltip="View claim"
 * />
 *
 * @example
 * // With search params
 * <Button.OpenInNewTab
 *   to="/console/$orgId/reports"
 *   params={{ orgId: '1' }}
 *   search={{ filter: 'active' }}
 * />
 *
 * @example
 * // Different colour and size
 * <Button.OpenInNewTab
 *   to="/console/$orgId/settings"
 *   params={{ orgId: '1' }}
 *   color="blue"
 *   size="small"
 * />
 */
const CreatedOpenInNewTabLink = createLink(OpenInNewTabBase);

/**
 * Wrapper that ensures target="_blank" is passed to TanStack Router
 * so it allows the browser to handle the navigation natively.
 */
export const ButtonOpenInNewTab: LinkComponent<typeof OpenInNewTabBase> = ((props) => {
    return <CreatedOpenInNewTabLink target="_blank" {...(props as any)} />;
}) as LinkComponent<typeof OpenInNewTabBase>;
